import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { getAllPosts, getPostData } from "../../utils/markdownLoader";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import BlogBottomCTA from "../../components/blogSection/bottomCTA";
import InListCTA from "../../components/blogSection/inlistCTA";
import "../../assets/css/Blog.css"; // We'll create this later

const BlogList = () => {
    const [posts, setPosts] = useState([]);
    const [categories, setCategories] = useState(["All"]);
    const [activeCategory, setActiveCategory] = useState("All");
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        // ... (fetch logic remains same) ...
        const fetchPosts = async () => {
            const allPostPaths = await getAllPosts();

            const loadedPosts = await Promise.all(
                allPostPaths.map(async (item) => {
                    const data = await getPostData(item.path);

                    // Resolve relative image path
                    let resolvedImage = data.image;
                    if (data.image && !data.image.startsWith('/') && !data.image.startsWith('http')) {
                        const parts = item.path.split('/');
                        parts.pop();
                        const dir = parts.join('/') + '/';
                        resolvedImage = dir + data.image;
                    }

                    return {
                        slug: item.slug,
                        ...data,
                        resolvedImage // Pass resolved path
                    };
                })
            );

            // Sort by date desc
            loadedPosts.sort((a, b) => new Date(b.date) - new Date(a.date));
            setPosts(loadedPosts);

            // Extract unique categories
            const uniqueCategories = ["All", ...new Set(loadedPosts.flatMap(p => p.categories || []))];
            setCategories(uniqueCategories);
        };

        fetchPosts();
    }, []);

    const filteredPosts = posts.filter(post => {
        const matchesCategory = activeCategory === "All" || (post.categories && post.categories.includes(activeCategory));
        const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            post.description.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    // Pagination Logic
    const [currentPage, setCurrentPage] = useState(1);
    const regularPostsPerPage = 6;

    // Calculate items to show based on page (Page 1 gets 5 posts + 1 CTA, others get 6 posts)
    let currentPosts;
    if (currentPage === 1) {
        currentPosts = filteredPosts.slice(0, 5);
    } else {
        const skip = 5 + (currentPage - 2) * regularPostsPerPage;
        currentPosts = filteredPosts.slice(skip, skip + regularPostsPerPage);
    }

    // Calculate total pages
    // First page takes 5. Remaining items take slots of 6.
    const remainingPosts = Math.max(0, filteredPosts.length - 5);
    const totalPages = 1 + Math.ceil(remainingPosts / regularPostsPerPage);

    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Reset pagination when category/search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [activeCategory, searchQuery]);

    // Calculate read time
    const calculateReadTime = (content) => {
        if (!content) return "1 min read";
        const wordsPerMinute = 200;
        const words = content.trim().split(/\s+/).length;
        const time = Math.ceil(words / wordsPerMinute);
        return `${time} min read`;
    };

    return (
        <div>
            <Helmet>
                <title>Latest Updates | Comparedly Blog</title>
                <meta name="description" content="Stay updated with the latest social media trends, benchmarks, and strategies." />
            </Helmet>
            <Navbar />
            <div className="blog-container" style={{ marginTop: "30px", padding: "20px", minHeight: "60vh" }}>
                <h1 style={{ textAlign: "center", marginBottom: "40px", color: "#00A1A1" }}>Latest Updates</h1>

                {/* Search & Filter Section */}
                <div className="blog-controls">
                    <input
                        type="text"
                        placeholder="Search posts..."
                        className="blog-search-input"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />

                    {/* Category Filter Bar */}
                    <div className="blog-filter-bar">
                        {categories.map(cat => (
                            <button
                                key={cat}
                                className={`filter-btn ${activeCategory === cat ? 'active' : ''}`}
                                onClick={() => setActiveCategory(cat)}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="blog-grid">
                    {/* Visual Logic: Insert CTA at position 3 (index 2) */}
                    {(() => {
                        // Create a new array with the CTA inserted if we have enough items
                        const displayItems = [...currentPosts];
                        /* Only insert CTA on the first page */
                        if (currentPage === 1 && displayItems.length >= 2) {
                            displayItems.splice(2, 0, { type: 'cta', id: 'cta-insert' });
                        }

                        return displayItems.map((item, index) => {
                            if (item.type === 'cta') {
                                return <InListCTA key="in-list-cta" />;
                            }

                            const post = item; // It's a regular post
                            const readTime = calculateReadTime(post.content);
                            return (
                                <Link
                                    to={`/blog/${post.slug}`}
                                    key={post.slug}
                                    className="blog-card"
                                    style={{ textDecoration: 'none', color: 'inherit' }}
                                >
                                    {post.image && (
                                        <img
                                            src={post.resolvedImage || post.image}
                                            alt={post.title}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                    )}
                                    <div className="blog-content-preview">
                                        <div className="blog-meta-small">
                                            {post.date} • {readTime} • {post.author}
                                        </div>
                                        <h3 className="blog-title-card">
                                            {post.title}
                                        </h3>
                                        <p className="blog-desc">{post.description}</p>
                                        <span className="read-more-btn">
                                            Read More →
                                        </span>
                                    </div>
                                </Link>
                            );
                        });
                    })()}
                </div>

                {/* Stylish Pagination */}
                {totalPages > 1 && (
                    <div className="pagination-container">
                        <button
                            className={`pagination-btn ${currentPage === 1 ? 'disabled' : ''}`}
                            onClick={() => paginate(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            &laquo; Prev
                        </button>

                        {[...Array(totalPages)].map((_, index) => (
                            <button
                                key={index}
                                className={`pagination-number ${currentPage === index + 1 ? 'active' : ''}`}
                                onClick={() => paginate(index + 1)}
                            >
                                {index + 1}
                            </button>
                        ))}

                        <button
                            className={`pagination-btn ${currentPage === totalPages ? 'disabled' : ''}`}
                            onClick={() => paginate(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        >
                            Next &raquo;
                        </button>
                    </div>
                )}
            </div>
            <BlogBottomCTA />
            <Footer />
        </div>
    );
};

export default BlogList;
